import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, FileTextIcon, LinkIcon, UsersIcon, SettingsIcon, DatabaseIcon, RefreshCwIcon, CheckCircleIcon, AlertCircleIcon, ExternalLinkIcon, DownloadIcon, EyeIcon, SparklesIcon, CalendarIcon, UserIcon, ShieldIcon, ClockIcon } from 'lucide-react';
import { getSpaceDetails } from '../lib/spacesClient';
import { KnowledgeSpaceDetails } from '../data/schema/knowledgeSpacesSchema';
import { toast } from 'sonner';
interface KnowledgeSpaceModalProps {
  spaceId: string;
  onClose: () => void;
}
export function KnowledgeSpaceModal({
  spaceId,
  onClose
}: KnowledgeSpaceModalProps) {
  const [space, setSpace] = useState<KnowledgeSpaceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'documents' | 'collaborators' | 'settings'>('documents');
  useEffect(() => {
    const loadSpace = async () => {
      setLoading(true);
      const data = await getSpaceDetails(spaceId);
      setSpace(data);
      setLoading(false);
    };
    loadSpace();
  }, [spaceId]);
  const handleSync = async () => {
    setSyncing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setSyncing(false);
    toast.success('Knowledge Space synced successfully', {
      description: 'All documents and links are up to date',
      duration: 3000
    });
  };
  const visibilityColor = space?.visibility === 'Internal' ? 'text-purple-400 bg-purple-500/10 border-purple-500/30' : 'text-blue-400 bg-blue-500/10 border-blue-500/30';
  const accessLevelColor = space?.accessLevel === 'admin' ? 'text-green-400 bg-green-500/10 border-green-500/30' : space?.accessLevel === 'write' ? 'text-blue-400 bg-blue-500/10 border-blue-500/30' : 'text-slate-400 bg-slate-500/10 border-slate-500/30';
  return <>
      {/* Backdrop */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-613" />
      {/* Modal Container - Expanded to use more screen space */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-614">
        <div className="relative w-[95vw] h-[92vh] max-w-[1920px] pointer-events-auto flex flex-col" data-id="element-615">
          {/* Modal Content */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800 rounded-2xl shadow-2xl flex flex-col h-full overflow-hidden" data-id="element-616">
            {loading ? <div className="flex-1 flex items-center justify-center" data-id="element-617">
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 1,
              repeat: Infinity,
              ease: 'linear'
            }} className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full" data-id="element-618" />
              </div> : !space ? <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-619">
                <div className="w-24 h-24 rounded-2xl bg-slate-800/60 flex items-center justify-center mb-6 shadow-lg" data-id="element-620">
                  <AlertCircleIcon className="w-12 h-12 text-slate-500" data-id="element-621" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-622">
                  Space Not Found
                </h3>
                <p className="text-slate-400 mb-6" data-id="element-623">
                  The requested Knowledge Space could not be loaded.
                </p>
                <button onClick={onClose} className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-all shadow-lg" data-id="element-624">
                  Close
                </button>
              </div> : <>
                {/* Enhanced Header */}
                <div className="relative flex items-center justify-between px-10 py-6 border-b border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 backdrop-blur-xl" data-id="element-625">
                  {/* Decorative gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" data-id="element-626" />
                  <div className="relative flex items-center gap-6 flex-1" data-id="element-627">
                    <div className={`w-16 h-16 rounded-2xl ${space.visibility === 'Internal' ? 'bg-gradient-to-br from-purple-600 to-purple-700' : 'bg-gradient-to-br from-blue-600 to-blue-700'} flex items-center justify-center shadow-2xl ring-2 ${space.visibility === 'Internal' ? 'ring-purple-500/30' : 'ring-blue-500/30'}`} data-id="element-628">
                      <DatabaseIcon className="w-8 h-8 text-white" data-id="element-629" />
                    </div>
                    <div data-id="element-630">
                      <div className="flex items-center gap-4 mb-2" data-id="element-631">
                        <h2 className="text-3xl font-bold text-white tracking-tight bg-gradient-to-r from-white via-blue-50 to-slate-200 bg-clip-text text-transparent" data-id="element-632">
                          {space.name}
                        </h2>
                        <span className={`px-4 py-1.5 rounded-xl text-xs font-bold border shadow-lg ${visibilityColor}`} data-id="element-633">
                          {space.visibility}
                        </span>
                        <span className={`px-4 py-1.5 rounded-xl text-xs font-bold border shadow-lg ${accessLevelColor}`} data-id="element-634">
                          {space.accessLevel}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 max-w-2xl leading-relaxed" data-id="element-635">
                        {space.description}
                      </p>
                    </div>
                  </div>
                  <div className="relative flex items-center gap-3" data-id="element-636">
                    <button onClick={handleSync} disabled={syncing} className="px-6 py-3 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/50 hover:border-slate-600 text-white text-sm font-semibold rounded-xl transition-all duration-200 flex items-center gap-2.5 shadow-xl shadow-black/30 hover:shadow-2xl hover:shadow-black/40 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95" data-id="element-637">
                      <RefreshCwIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} data-id="element-638" />
                      {syncing ? 'Syncing...' : 'Sync Now'}
                    </button>
                    <button onClick={onClose} className="p-3 hover:bg-slate-800/60 rounded-xl transition-all duration-200 text-slate-400 hover:text-white hover:scale-105" data-id="element-639">
                      <XIcon className="w-6 h-6" data-id="element-640" />
                    </button>
                  </div>
                </div>
                {/* Enhanced Stats Bar */}
                <div className="relative px-10 py-6 border-b border-slate-800/80 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 backdrop-blur-xl" data-id="element-641">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-transparent to-blue-500/5 pointer-events-none" data-id="element-642" />
                  <div className="relative grid grid-cols-4 gap-6" data-id="element-643">
                    <div className="group relative bg-slate-950/60 rounded-xl p-5 border border-slate-800/60 hover:border-slate-700/60 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden" data-id="element-644">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-645" />
                      <div className="relative" data-id="element-646">
                        <div className="flex items-center justify-between mb-3" data-id="element-647">
                          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors" data-id="element-648">
                            <FileTextIcon className="w-5 h-5 text-blue-400" data-id="element-649" />
                          </div>
                          <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-650">
                            {space.documentCount}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider font-bold" data-id="element-651">
                          Documents
                        </p>
                      </div>
                    </div>
                    <div className="group relative bg-slate-950/60 rounded-xl p-5 border border-slate-800/60 hover:border-slate-700/60 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden" data-id="element-652">
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-653" />
                      <div className="relative" data-id="element-654">
                        <div className="flex items-center justify-between mb-3" data-id="element-655">
                          <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors" data-id="element-656">
                            <UsersIcon className="w-5 h-5 text-purple-400" data-id="element-657" />
                          </div>
                          <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-658">
                            {space.collaborators.length}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider font-bold" data-id="element-659">
                          Collaborators
                        </p>
                      </div>
                    </div>
                    <div className="group relative bg-slate-950/60 rounded-xl p-5 border border-slate-800/60 hover:border-slate-700/60 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden" data-id="element-660">
                      <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-661" />
                      <div className="relative" data-id="element-662">
                        <div className="flex items-center justify-between mb-3" data-id="element-663">
                          <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors" data-id="element-664">
                            <SparklesIcon className="w-5 h-5 text-green-400" data-id="element-665" />
                          </div>
                          <span className="text-3xl font-bold text-white group-hover:scale-110 transition-transform" data-id="element-666">
                            {space.boundBots.length}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider font-bold" data-id="element-667">
                          Bound Bots
                        </p>
                      </div>
                    </div>
                    <div className="group relative bg-slate-950/60 rounded-xl p-5 border border-slate-800/60 hover:border-slate-700/60 transition-all duration-300 shadow-lg hover:shadow-xl overflow-hidden" data-id="element-668">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-669" />
                      <div className="relative" data-id="element-670">
                        <div className="flex items-center justify-between mb-3" data-id="element-671">
                          <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors" data-id="element-672">
                            <ClockIcon className="w-5 h-5 text-amber-400" data-id="element-673" />
                          </div>
                          <span className="text-sm font-semibold text-white" data-id="element-674">
                            {new Date(space.lastUpdated).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider font-bold" data-id="element-675">
                          Last Updated
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Enhanced Tabs */}
                <div className="relative px-10 py-4 border-b border-slate-800/80 bg-slate-950/50" data-id="element-676">
                  <div className="flex gap-2" data-id="element-677">
                    {(['documents', 'collaborators', 'settings'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${activeTab === tab ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-xl shadow-blue-600/40 ring-2 ring-blue-500/40 scale-[1.02]' : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700/60 hover:text-white border border-slate-700/50 hover:border-slate-600 hover:scale-[1.02]'}`} data-id="element-678">
                          {tab === 'documents' && <FileTextIcon className="w-4 h-4 inline mr-2" data-id="element-679" />}
                          {tab === 'collaborators' && <UsersIcon className="w-4 h-4 inline mr-2" data-id="element-680" />}
                          {tab === 'settings' && <SettingsIcon className="w-4 h-4 inline mr-2" data-id="element-681" />}
                          {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        </button>)}
                  </div>
                </div>
                {/* Enhanced Content */}
                <div className="flex-1 overflow-y-auto p-10 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent" data-id="element-682">
                  <AnimatePresence mode="wait" data-id="element-683">
                    {activeTab === 'documents' && <motion.div key="documents" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-684">
                        {space.documents.length === 0 ? <div className="text-center py-16" data-id="element-685">
                            <div className="w-20 h-20 rounded-2xl bg-slate-800/60 flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-686">
                              <FileTextIcon className="w-10 h-10 text-slate-500" data-id="element-687" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2" data-id="element-688">
                              No Documents
                            </h3>
                            <p className="text-slate-400" data-id="element-689">
                              This Knowledge Space does not have any documents
                              yet.
                            </p>
                          </div> : space.documents.map((doc, index) => <motion.div key={doc.id} initial={{
                    opacity: 0,
                    x: -20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: index * 0.05
                  }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-690">
                              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-691" />
                              <div className="relative flex items-start justify-between gap-4" data-id="element-692">
                                <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-693">
                                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg ring-2 ring-blue-500/20 group-hover:scale-110 transition-all duration-300 flex-shrink-0" data-id="element-694">
                                    <FileTextIcon className="w-6 h-6 text-white" data-id="element-695" />
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-696">
                                    <h4 className="text-base font-bold text-white mb-2 truncate group-hover:text-blue-400 transition-colors" data-id="element-697">
                                      {doc.title}
                                    </h4>
                                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400" data-id="element-698">
                                      <span className="flex items-center gap-1.5" data-id="element-699">
                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400" data-id="element-700" />
                                        {doc.type}
                                      </span>
                                      <span className="flex items-center gap-1.5" data-id="element-701">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" data-id="element-702" />
                                        {(doc.size / 1024 / 1024).toFixed(2)} MB
                                      </span>
                                      <span className="flex items-center gap-1.5" data-id="element-703">
                                        <UserIcon className="w-3 h-3" data-id="element-704" />
                                        {doc.uploadedBy}
                                      </span>
                                      <span className="flex items-center gap-1.5" data-id="element-705">
                                        <CalendarIcon className="w-3 h-3" data-id="element-706" />
                                        {new Date(doc.uploadedAt).toLocaleDateString()}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0" data-id="element-707">
                                  <button className="p-2.5 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 text-slate-400 hover:text-white rounded-lg transition-all shadow-lg hover:shadow-xl hover:scale-105" data-id="element-708">
                                    <EyeIcon className="w-4 h-4" data-id="element-709" />
                                  </button>
                                  <button className="p-2.5 bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/50 hover:border-slate-600 text-slate-400 hover:text-white rounded-lg transition-all shadow-lg hover:shadow-xl hover:scale-105" data-id="element-710">
                                    <DownloadIcon className="w-4 h-4" data-id="element-711" />
                                  </button>
                                </div>
                              </div>
                            </motion.div>)}
                      </motion.div>}
                    {activeTab === 'collaborators' && <motion.div key="collaborators" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.3
                }} className="space-y-4" data-id="element-712">
                        {space.collaborators.map((collab, index) => <motion.div key={collab.id} initial={{
                    opacity: 0,
                    x: -20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: index * 0.05
                  }} className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-713">
                            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-714" />
                            <div className="relative flex items-center justify-between gap-4" data-id="element-715">
                              <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-716">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center text-white text-lg font-bold shadow-lg ring-2 ring-purple-500/20 group-hover:scale-110 transition-all duration-300 flex-shrink-0" data-id="element-717">
                                  {collab.name.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-718">
                                  <h4 className="text-base font-bold text-white mb-1 truncate group-hover:text-purple-400 transition-colors" data-id="element-719">
                                    {collab.name}
                                  </h4>
                                  <p className="text-sm text-slate-400 truncate" data-id="element-720">
                                    {collab.role}
                                  </p>
                                </div>
                              </div>
                              <span className={`px-4 py-2 rounded-xl text-xs font-bold border shadow-lg ${collab.accessLevel === 'admin' ? 'text-green-400 bg-green-500/10 border-green-500/30' : collab.accessLevel === 'write' ? 'text-blue-400 bg-blue-500/10 border-blue-500/30' : 'text-slate-400 bg-slate-500/10 border-slate-500/30'}`} data-id="element-721">
                                <ShieldIcon className="w-3 h-3 inline mr-1" data-id="element-722" />
                                {collab.accessLevel}
                              </span>
                            </div>
                          </motion.div>)}
                      </motion.div>}
                    {activeTab === 'settings' && <motion.div key="settings" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.3
                }} className="space-y-6" data-id="element-723">
                        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-724">
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-725" />
                          <div className="relative" data-id="element-726">
                            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-3" data-id="element-727">
                              <SettingsIcon className="w-5 h-5 text-blue-400" data-id="element-728" />
                              Space Settings
                            </h3>
                            <div className="space-y-5" data-id="element-729">
                              <div className="flex items-center justify-between py-4 border-b border-slate-800/60" data-id="element-730">
                                <div data-id="element-731">
                                  <p className="text-sm font-semibold text-white mb-1" data-id="element-732">
                                    External Access
                                  </p>
                                  <p className="text-xs text-slate-400" data-id="element-733">
                                    Allow access from outside the organization
                                  </p>
                                </div>
                                <div className={`px-4 py-2 rounded-xl text-xs font-bold ${space.settings.allowExternalAccess ? 'text-green-400 bg-green-500/10 border border-green-500/30' : 'text-red-400 bg-red-500/10 border border-red-500/30'}`} data-id="element-734">
                                  {space.settings.allowExternalAccess ? <>
                                      <CheckCircleIcon className="w-3 h-3 inline mr-1" data-id="element-735" />
                                      Enabled
                                    </> : <>
                                      <XIcon className="w-3 h-3 inline mr-1" data-id="element-736" />
                                      Disabled
                                    </>}
                                </div>
                              </div>
                              <div className="flex items-center justify-between py-4 border-b border-slate-800/60" data-id="element-737">
                                <div data-id="element-738">
                                  <p className="text-sm font-semibold text-white mb-1" data-id="element-739">
                                    Require Approval
                                  </p>
                                  <p className="text-xs text-slate-400" data-id="element-740">
                                    All changes must be approved by an admin
                                  </p>
                                </div>
                                <div className={`px-4 py-2 rounded-xl text-xs font-bold ${space.settings.requireApproval ? 'text-amber-400 bg-amber-500/10 border border-amber-500/30' : 'text-slate-400 bg-slate-500/10 border border-slate-500/30'}`} data-id="element-741">
                                  {space.settings.requireApproval ? <>
                                      <CheckCircleIcon className="w-3 h-3 inline mr-1" data-id="element-742" />
                                      Required
                                    </> : 'Not Required'}
                                </div>
                              </div>
                              <div className="flex items-center justify-between py-4" data-id="element-743">
                                <div data-id="element-744">
                                  <p className="text-sm font-semibold text-white mb-1" data-id="element-745">
                                    Data Retention
                                  </p>
                                  <p className="text-xs text-slate-400" data-id="element-746">
                                    Documents older than this will be archived
                                  </p>
                                </div>
                                <div className="px-4 py-2 rounded-xl text-xs font-bold text-blue-400 bg-blue-500/10 border border-blue-500/30" data-id="element-747">
                                  {space.settings.retentionDays} days
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="group relative bg-gradient-to-br from-slate-900/90 via-slate-900/80 to-slate-950/90 backdrop-blur-xl border border-slate-800/80 rounded-2xl p-6 shadow-2xl shadow-black/30 hover:border-slate-700/80 transition-all duration-300 overflow-hidden" data-id="element-748">
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-749" />
                          <div className="relative" data-id="element-750">
                            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-3" data-id="element-751">
                              <SparklesIcon className="w-5 h-5 text-purple-400" data-id="element-752" />
                              Bound Chatbots
                            </h3>
                            <div className="space-y-3" data-id="element-753">
                              {space.boundBots.map((bot, index) => <div key={index} className="flex items-center gap-3 p-4 bg-slate-950/60 rounded-xl border border-slate-800/60 hover:border-slate-700/60 transition-all" data-id="element-754">
                                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg" data-id="element-755">
                                    <SparklesIcon className="w-5 h-5 text-white" data-id="element-756" />
                                  </div>
                                  <span className="text-sm font-semibold text-white" data-id="element-757">
                                    {bot}
                                  </span>
                                </div>)}
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </>}
          </div>
        </div>
      </motion.div>
    </>;
}